import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query';
import React, { useEffect, useMemo, useRef } from 'react';

import { type Entity, identityEntityGetEntityOptions } from '@column/api';
import { Icon } from '@column/column-ui-kit';
import { Badge, DataTable, type DataTableColumnDef } from '@column/ui';

import { ROUTE } from '~/app/routes';
import { Loading } from '~/components';
import { Empty as TableEmpty, getTimestampColumn, tableDefaults, useTimeFormat } from '~/components/Table';
import { usePaginationState } from '~/lib/api/hooks/usePaginationState';
import { useNavigate } from '~/lib/navigation';
import { CardProgramRepository } from '~/repositories/CardProgramRepository';
import { Card, CardRepository } from '~/repositories/CardRepository';
import { useSessionStore } from '~/stores/Session';
import { Inner } from '~/styles';
import { formatString } from '~/util';

const formatExpires = (card: Card): string => card.expiresAt || '-';

export const PageCardsOverview: React.FC = () => {
  const navigate = useNavigate();
  const { currentPlatform, isSandbox } = useSessionStore();
  const {
    cursor,
    hasPrevious,
    onNext,
    onPrevious,
    pageIndex,
    pageSize,
    reset: resetPagination,
    setPageSize,
  } = usePaginationState();
  const { timeFormat, onTimeFormatChange } = useTimeFormat('cards');

  // Reset the cursor when the platform or sandbox mode changes; otherwise the
  // old record id gets sent as starting_after against a different dataset.
  const isInitialRenderRef = useRef(true);
  useEffect(() => {
    if (isInitialRenderRef.current) {
      isInitialRenderRef.current = false;
      return;
    }
    resetPagination();
  }, [currentPlatform?.id, isSandbox, resetPagination]);

  const {
    data: cardsData,
    isLoading: isLoadingCards,
    isFetching: isFetchingCards,
    isError: isCardsError,
  } = useQuery({
    queryKey: ['cards', 'list', currentPlatform?.id, isSandbox, pageSize, cursor],
    queryFn: () => CardRepository.listCards({ limit: pageSize, startingAfter: cursor }),
    enabled: Boolean(currentPlatform?.id),
    placeholderData: keepPreviousData,
  });

  const { data: programsData } = useQuery({
    queryKey: ['card-programs', 'list-all', currentPlatform?.id, isSandbox],
    queryFn: () => CardProgramRepository.listCardPrograms(),
    enabled: Boolean(currentPlatform?.id),
  });

  const cards = cardsData?.cards ?? [];
  const hasMore = Boolean(cardsData?.hasMore);

  const programsById = useMemo(() => {
    const map = new Map<string, string>();
    (programsData?.cardPrograms ?? []).forEach((program) => {
      if (program.description) {
        map.set(program.id, program.description);
      }
    });
    return map;
  }, [programsData]);

  const uniqueEntityIds = useMemo(
    () => Array.from(new Set(cards.map((c) => c.authorizedUserEntityId).filter((id): id is string => Boolean(id)))),
    [cards]
  );

  const entityQueries = useQueries({
    queries: uniqueEntityIds.map((entityId) => ({
      ...identityEntityGetEntityOptions({ path: { entity_id: entityId } }),
      enabled: Boolean(currentPlatform?.id),
    })),
  });

  const entitiesById = useMemo(() => {
    const map = new Map<string, Entity>();
    entityQueries.forEach((query, i) => {
      if (query.data) map.set(uniqueEntityIds[i], query.data);
    });
    return map;
  }, [entityQueries, uniqueEntityIds]);

  const getAuthorizedUserName = (entityId: string): string => {
    const entity = entitiesById.get(entityId);
    const first = entity?.person_details?.first_name ?? '';
    const last = entity?.person_details?.last_name ?? '';
    const full = `${first} ${last}`.trim();
    return full || entity?.name || '-';
  };

  const columns: DataTableColumnDef<Card>[] = [
    {
      header: 'Authorized User',
      accessor: 'authorizedUserEntityId',
      width: 'minmax(min-content, 1fr)',
      cell: ({ row }) => (
        <DataTable.TextCell weight="medium" value={getAuthorizedUserName(row.authorizedUserEntityId)} />
      ),
    },
    {
      header: 'Card Program',
      accessor: 'cardProgramId',
      width: 'minmax(min-content, 1fr)',
      cell: ({ row }) => <DataTable.TextCell value={programsById.get(row.cardProgramId) || row.cardProgramId || '-'} />,
    },
    {
      header: 'BIN',
      accessor: 'bin',
      width: 'min-content',
      cell: ({ value }) => <DataTable.TextCell value={(value as string) || '-'} />,
    },
    {
      header: 'Last 4',
      accessor: 'lastFourDigits',
      width: 'min-content',
      cell: ({ value }) => <DataTable.TextCell value={(value as string) || '-'} />,
    },
    {
      header: 'Expires',
      id: 'expires',
      width: 'min-content',
      cell: ({ row }) => <DataTable.TextCell value={formatExpires(row)} />,
    },
    {
      header: 'Status',
      accessor: 'status',
      width: 'min-content',
      cell: ({ value }) => <Badge variant="secondary">{value ? formatString(value as string) : '-'}</Badge>,
    },
    {
      header: 'Type',
      accessor: 'type',
      width: 'min-content',
      cell: ({ value }) => <Badge variant="secondary">{value ? formatString(value as string) : '-'}</Badge>,
    },
    getTimestampColumn<Card>({ accessor: 'createdAt', timeFormat, onTimeFormatChange, fallback: '-' }),
  ];

  return (
    <>
      <Loading isLoading={isLoadingCards}>
        <Inner pt={0} px="var(--page-margin)">
          <DataTable<Card>
            {...tableDefaults}
            columns={columns}
            data={isCardsError ? [] : cards}
            rowKey={(row) => row.id}
            isLoading={isFetchingCards}
            emptyContent={
              isCardsError ? (
                <TableEmpty icon={<Icon.Warning />} title="Failed to load cards" />
              ) : (
                <TableEmpty
                  icon={<Icon.AnimationWallet />}
                  title="No cards found"
                  description="Cards will appear here once they're created."
                />
              )
            }
            pagination={{
              hasNext: hasMore,
              hasPrevious,
              pageIndex,
              pageSize,
              onNext: () => {
                if (cards.length > 0) {
                  onNext({ cursor: cards[cards.length - 1].id });
                }
              },
              onPrevious,
              onPageSizeChange: setPageSize,
            }}
            onRowClick={(row) => {
              navigate(`${ROUTE.CARDS}/edit/${row.id}`);
            }}
          />
        </Inner>
      </Loading>
    </>
  );
};
